// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';
import { isRecaptchaConfigured } from './recaptcha';

/** Evita `//api` al concatenar con rutas que empiezan por `/`. */
const normalizeRootUrl = raw =>
  raw == null || raw === ''
    ? ''
    : String(raw)
        .trim()
        .replace(/\/+$/, '');

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? normalizeRootUrl(marketplaceRootURL) : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Read-only validation of a discount code for the checkout UX. Returns
// { valid, reason, minBookingAmount } so the UI can explain why a code isn't
// applied. Has no side effects (does not consume a use).
//
// See `server/api/validate-discount-code.js`.
export const validateDiscountCode = body => {
  return post('/api/validate-discount-code', body);
};

// Initiate an inquiry transaction through the backend so the A/B cookie
// is read server-side and the resulting tx gets `metadata.experiments`.
//
// See `server/api/initiate-inquiry.js`.
export const initiateInquiry = body => {
  return post('/api/initiate-inquiry', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Recompute the host's `hasReceivedBooking` flag after a booking completes.
// The server verifies eligibility authoritatively; this is fire-and-forget on
// the client and only used to keep the search badge in sync.
//
// See `server/api/reconcile-host-booking-flag.js`.
export const reconcileHostBookingFlag = body => {
  return post('/api/reconcile-host-booking-flag', body);
};

// Fetch a listing's exact address for a confirmed booking. The server verifies
// the caller is a transaction participant and the booking has been accepted
// before returning the author-only address.
//
// See `server/api/listing-address.js`.
export const fetchListingAddress = body => {
  return post('/api/listing-address', body);
};

// Fetch the other participant's phone number for a confirmed booking. The server
// verifies the caller is a transaction participant and the booking has been
// accepted before returning the author-only phone number.
//
// See `server/api/contact-phone.js`.
export const fetchContactPhone = body => {
  return post('/api/contact-phone', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

/** Integration API vía backend: ¿existe usuario con este email? */
export const checkUserExistsByEmail = email => {
  return fetch(`${apiBaseUrl()}/api/auth/check-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  }).then(response =>
    response.json().then(data => ({ ok: response.ok, status: response.status, ...data }))
  );
};

export const updateUserListing = body => {
  return post('/api/updateUserListing', body);
};

export const updateListingMetadata = body => {
  return post('/api/updateListingMetadata', body);
};
export const onUpdateUserProfile = body => {
  return post('/api/onUpdateUserProfile', body);
};
export const averageRatings = body => {
  return post('/api/averageRatings', body);
};

// Send customer/provider Invoice emails to admin
export const sendCustomerInvoiceToAdmin = body => {
  return post('/api/customerInvoiceToAdmin', body);
};
export const sendProviderInvoiceToAdmin = body => {
  return post('/api/providerInvoiceToAdmin', body);
};
export const getIntercomToken = body => {
  return post('/api/generate-intercom-token', body);
};
//update transaction metadata
export const updateTransaction = body => {
  return post('/api/updateTransaction', body);
};
// block a listing date by creating an availability exception with seats: 0
export const blockListingDate = body => {
  return post('/api/blockListingDate', body);
};

// stripe refund and reversal
export const partialRefundAndReversal = body => {
  return post('/api/partialRefundAndReversal', body);
};

// signup honeypot trap report (best-effort; never block the caller)
export const reportHoneypotTrap = body => {
  return post('/api/honeypotTrap', body).catch(() => {});
};

// Record the IP + user-agent the just-registered (and now authenticated) user
// signed up from. Best-effort: must never block or fail the signup flow.
export const recordSignupContext = () => {
  return post('/api/record-signup-context', {}).catch(() => {});
};

// Record the IP + user-agent of an email/password login into the user's own
// privateData.lastLogin. It goes through the server because the browser cannot
// see its own IP; the IdP flows stamp the same record in their own handlers.
// Best-effort: must never surface as a failed login.
export const recordLastLogin = () => {
  return post('/api/record-last-login', {}).catch(() => {});
};

// Verify a reCAPTCHA v3 token server-side. Resolves to `true` when the signup
// may proceed. When reCAPTCHA is not configured on the client we skip the call
// entirely (fail-open); otherwise we trust the server's verdict and block on a
// failed verification or any error.
export const verifyCaptcha = (token, email) => {
  if (!isRecaptchaConfigured()) {
    return Promise.resolve(true);
  }
  return post('/api/verify-captcha', { token, action: 'signup', email })
    .then(res => !!res?.success)
    .catch(() => false);
};

//aws file upload

export const getCityFromLocationApi = body => {
  return post('/api/getCityFromLocation', body);
};

export const onUpdateUserAverageRatings = body => {
  return post('/api/updateUserAverageRatings', body);
};

export const saveSearchHistory = body => {
  return post('/api/saveSearchHistory', body);
};

// Read-only province occupancy for the search "high demand" banner. Returns
// { percentage } (rounded 0–100) or { percentage: null } when unknown. Best-effort:
// the endpoint never 5xxs, and callers treat any failure as "no banner".
//
// See `server/api/province-occupancy.js`.
export const getProvinceOccupancy = ({ province, date }) => {
  const qs = new URLSearchParams({ province, date }).toString();
  return request(`/api/province-occupancy?${qs}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

// An SEO landing as the daily job stored it (cards, reviews, prices, nearby municipalities), or
// null when the page does not exist. Also called during SSR, where there is no `window`: the
// server passes `ssrApiBaseUrl` (itself, on localhost); the browser uses the usual API base.
// See `server/api/seo-landing.js`.
export const getSeoLanding = (path, ssrApiBaseUrl) => {
  const qs = new URLSearchParams({ path }).toString();
  return fetch(`${ssrApiBaseUrl || apiBaseUrl()}/api/seo-landing?${qs}`).then(res => {
    if (res.status === 404) {
      return null;
    }
    if (!res.ok) {
      throw new Error(`seo-landing ${res.status}`);
    }
    return res.text().then(deserialize);
  });
};

// The SEO landings that exist for a municipality (page slugs), for the search page's canonical.
// Best-effort: any failure is "none". Also called during SSR, like getSeoLanding.
export const getSeoLandingPages = (citySlug, ssrApiBaseUrl) => {
  const qs = new URLSearchParams({ city: citySlug }).toString();
  // Dentro de la promesa: así hasta un fallo síncrono (sin `fetch`, sin `window`) es "ninguna".
  return Promise.resolve()
    .then(() => fetch(`${ssrApiBaseUrl || apiBaseUrl()}/api/seo-landing/pages?${qs}`))
    .then(res => (res.ok ? res.json() : { pages: [] }))
    .then(body => (Array.isArray(body?.pages) ? body.pages : []))
    .catch(() => []);
};

// `sdk.listings.query` served from our own mirror of Sharetribe (docs/mirror-search.md): same
// params in, same `{ data: { data, included, meta } }` out, 0 credits. Rejects (with `status`
// and `reason`) whenever the server cannot answer exactly like Sharetribe would — mirror off,
// stale, or a query it cannot emulate — and the caller falls back to the SDK. Also called during
// SSR, like getSeoLanding.
const queryMirror = (path, params, ssrApiBaseUrl) =>
  Promise.resolve()
    .then(() =>
      fetch(`${ssrApiBaseUrl || apiBaseUrl()}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/transit+json' },
        body: serialize(params),
      })
    )
    .then(res => {
      if (!res.ok) {
        return res
          .json()
          .catch(() => ({}))
          .then(body => {
            throw Object.assign(new Error(`mirror ${res.status}`), {
              status: res.status,
              reason: body.reason,
            });
          });
      }
      return res.text().then(text => ({ data: deserialize(text) }));
    });

export const searchMirrorListings = (params, ssrApiBaseUrl) =>
  queryMirror('/api/search-listings', params, ssrApiBaseUrl);

// `sdk.reviews.query` from the mirror, same contract as searchMirrorListings (a user's public
// reviews, for the listing page; docs/mirror-search.md §8).
export const queryMirrorReviews = (params, ssrApiBaseUrl) =>
  queryMirror('/api/query-reviews', params, ssrApiBaseUrl);

// `sdk.listings.show` from the mirror, same contract (a published listing for the listing page;
// docs/mirror-search.md §9). The response is `{ data: { data, included } }`, like the SDK's.
export const showMirrorListing = (params, ssrApiBaseUrl) =>
  queryMirror('/api/show-listing', params, ssrApiBaseUrl);

// Fire-and-forget report of a mirror-vs-Sharetribe comparison (shadow mode). Never rejects.
export const reportMirrorShadow = body =>
  Promise.resolve()
    .then(() =>
      fetch(`${apiBaseUrl()}/api/mirror-shadow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
    )
    .catch(() => {});

// External ("off-platform") bookings — see docs/external-bookings.md.
// These endpoints speak plain JSON (no Sharetribe SDK types involved).
//
// NOTE: they do NOT go through `request()`. It destructures `body` out of the
// options and only puts it back when it serialises to Transit, so a JSON body is
// silently DROPPED — the server would receive an empty object. Raw `fetch` is the
// established way to call a JSON endpoint here (see checkUserExistsByEmail).

const jsonHeaders = { 'Content-Type': 'application/json' };

/**
 * POST plain JSON, raising the same error shape `request()` does so
 * `storableError` keeps working (message in `statusText`).
 */
const postJson = (path, body) =>
  fetch(`${apiBaseUrl()}${path}`, {
    method: 'POST',
    headers: jsonHeaders,
    credentials: 'include',
    body: JSON.stringify(body || {}),
  }).then(res =>
    res.json().then(data => {
      if (res.status >= 400) {
        throw Object.assign(new Error(data?.error || 'Request failed'), {
          status: res.status,
          statusText: data?.error || data?.statusText,
          ...data,
        });
      }
      return data;
    })
  );

// Host creates a booking agreed off-platform. The host is resolved server-side from
// the session, so no host id is (or should be) sent.
export const createExternalBooking = body => postJson('/api/external-bookings', body);

// Cardholder name of the current user's own saved card, from Stripe:
// `{ name }`, null when there is nothing to show. See server/api/saved-card-holder.js.
export const getSavedCardHolder = () => {
  return request('/api/saved-card-holder', { method: 'GET', headers: jsonHeaders });
};

// The authenticated host's own external bookings. Returns
// `{ enabled: false, bookings: [] }` when the feature has no database behind it —
// callers hide the UI rather than showing an error.
// The signed-in host's superhost progress for the dashboard ({ criteria, metrics });
// 404 when there is none. See server/api/superhosts/me.js.
export const getSuperhostProgress = () => {
  return request('/api/superhost/me', { method: 'GET', headers: jsonHeaders });
};

export const getExternalBookings = () => {
  return request('/api/external-bookings', { method: 'GET', headers: jsonHeaders });
};

export const cancelExternalBooking = ({ id, reason }) =>
  postJson('/api/external-bookings/cancel', { id, reason });

// Guest-side, unauthenticated: the payment-link token is the only credential.
// The response is redacted (no host contact details, no money split).
export const getExternalBookingByToken = token => {
  const qs = new URLSearchParams({ token }).toString();
  return request(`/api/external-bookings/by-token?${qs}`, {
    method: 'GET',
    headers: jsonHeaders,
  });
};

// Mints the Stripe Checkout Session when the guest presses "Pagar" and returns
// `{ url }` to redirect to. The amount comes from the stored booking, never from here.
export const createExternalBookingCheckoutSession = token => {
  return request('/api/external-bookings/checkout-session', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ token }),
  });
};

// Temporary closures — schedule (or clear) a listing's automatic reopening.
// `reopenAt` is 'YYYY-MM-DD' or null. The host is resolved from the session, so
// only the listing's author can call this. See docs/temporary-closure.md.
export const scheduleListingReopen = ({ listingId, reopenAt, presetKey }) =>
  postJson('/api/listing/schedule-reopen', { listingId, reopenAt, presetKey });
